import { createTheme, type Theme } from '@mui/material/styles';
import type {} from '@mui/x-data-grid/themeAugmentation';

export type ColorMode = 'dark' | 'light';
export type ThemeMode = ColorMode | 'system';

export const DEFAULT_PRIMARY = '#FF4630';

export function buildTheme(primaryColor: string, mode: ColorMode = 'dark'): Theme {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryColor,
      },
      secondary: {
        main: '#ec4899',
        light: '#f472b6',
        dark: '#db2777',
      },
      background: isDark
        ? { default: '#0f172a', paper: '#1e293b' }
        : { default: '#f1f5f9', paper: '#ffffff' },
      text: isDark
        ? { primary: '#f1f5f9', secondary: '#94a3b8' }
        : { primary: '#1e293b', secondary: '#64748b' },
      divider: isDark ? '#334155' : '#e2e8f0',
      error: { main: '#ef4444' },
      warning: { main: '#f59e0b' },
      success: { main: '#22c55e' },
      info: { main: '#3b82f6' },
      ...(isDark && {
        grey: {
          50: '#1e293b',
          100: '#253347',
          200: '#334155',
          300: '#475569',
          400: '#64748b',
          500: '#94a3b8',
          600: '#cbd5e1',
          700: '#e2e8f0',
          800: '#f1f5f9',
          900: '#f8fafc',
          A100: '#253347',
          A200: '#334155',
          A400: '#64748b',
          A700: '#e2e8f0',
        },
      }),
    },
    typography: {
      fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
    },
    shape: { borderRadius: 10 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600 },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: { backgroundColor: 'transparent' },
        },
      },
      MuiTable: {
        styleOverrides: {
          root: { backgroundColor: 'transparent' },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: isDark ? '#334155' : '#e2e8f0',
          },
          head: {
            fontWeight: 700,
            color: isDark ? '#f1f5f9' : '#1e293b',
            backgroundColor: isDark ? '#253347' : '#f8fafc',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-child td, &:last-child th': {
              borderBottom: 0,
            },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? '#f1f5f9' : '#1e293b',
            color: isDark ? '#0f172a' : '#f1f5f9',
            fontSize: '0.8rem',
            fontWeight: 500,
          },
          arrow: {
            color: isDark ? '#f1f5f9' : '#1e293b',
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { borderRight: `1px solid ${isDark ? '#334155' : '#e2e8f0'}` },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: { border: 'none' },
        },
      },
    },
  });
}

export function getSystemMode(): ColorMode {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'dark';
}

export function getInitialMode(): ThemeMode {
  const saved = localStorage.getItem('themeMode');
  if (saved === 'dark' || saved === 'light' || saved === 'system') return saved;
  return 'system';
}